import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	inject,
	OnDestroy,
	OnInit,
	ViewChild,
	ChangeDetectionStrategy,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
	ActivatedRoute,
	NavigationEnd,
	NavigationExtras,
	Router,
} from '@angular/router';

import { GlobalClientCreateComponent } from '../../layouts/global-clients-create/global-clients-create.component';
import { JepSnackbarComponent } from '@jepto/shared/components/jep-snackbar/jep-snackbar.component';
import { JepClientsService } from '@jepto/shared/services/jep-clients/jep-clients.service';

// Store
import * as fromPortfolioModuleRootStoreAsFeature from '@jepto/features/portfolio/store/index';
import * as fromSharedAccountStore from '@jepto/shared/store/account/index';
import * as fromSharedStore from '@jepto/shared/store/index';
import { ClientStore } from '@jepto/shared/store/client/client.store';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { DropdownToggle } from '@jepto/shared/animations/jep-animations';

import { includes, isEmpty } from 'lodash';
import { EMPTY, Observable, Subject, combineLatest } from 'rxjs';
import {
	catchError,
	distinctUntilChanged,
	filter,
	switchMap,
	takeUntil,
	tap,
	map,
	withLatestFrom,
	startWith,
	take,
} from 'rxjs/operators';

import {
	clientIdFromIdOrSortKeyTail,
	dynamicKeySort,
	sortKeySplitter,
} from '@jepto/shared/utils/collection';
import { log } from '@jepto/shared/utils/logger';
import { stringContains } from '@jepto/shared/utils/regex';
import { Client } from '@jepto/shared/models/client.model';
import {
	markForCheckOnEmit,
	safeUpdateInSubscription,
} from '@jepto/shared/utils/change-detection';
import {
	buildClientReportsRouteOnSwitch,
	buildGlobalReportsRoute,
} from './global-clients-dropdown-reports-routing.util';
import {
	RouteClientLookup,
	RouteClientLookupService,
} from './route-client-lookup.service';

/**
 * Route configuration constant for managing valid services and features
 * This centralizes all routing logic and makes it easier to maintain
 */
const ROUTE_CONFIG = {
	// Valid main feature patterns that can be extracted from URLs
	validFeatures: [
		'kpis',
		'budgets',
		'data-sources',
		'anomalies',
		'insights',
		'automations',
		'settings',
		'local',
		'reviews',
		'posts',
		'reports',
	],

	// Valid services for each feature type
	validServices: {
		kpis: [
			'gsc',
			'gmb',
			'mads',
			'sheets',
			'fbads',
			'ga4',
			'tads',
			'lads',
			'twads',
			'igads',
			'gads',
			'ga',
		],
		budgets: [
			'gads',
			'fbads',
			'sheets',
			'mads',
			'tads',
			'lads',
			'twads',
			'igads',
		],
		automations: ['gsc', 'gmb', 'gads', 'ga', 'web', 'fbads', 'mads'],
		anomalies: [
			'ga',
			'gsc',
			'ga4',
			'gads',
			'mads',
			'fbads',
			'tads',
			'twads',
			'lads',
			'igads',
			'gmb',
			'sheets',
		],
		insights: ['gsc', 'gmb', 'gads', 'ga', 'fbads', 'mads'],
		reports: [],
		local: ['gmb'],
		reviews: ['gmb'],
		posts: ['fbads', 'igads', 'twads'],
	},

	// Special route configurations
	specialRoutes: {
		'data-sources': {
			subRoutes: ['connections', 'mcp-connectors', 'data-model'],
		},
		settings: {
			defaultSubRoute: 'connections',
		},
	},

	// Job ID pattern for automation workflows
	jobIdPattern: /^\d{3}$/,

	// Post ID pattern for social posts
	postIdPattern: /^pid_[a-zA-Z0-9]+$/,

	// Valid actions for automation jobs and posts
	validActions: ['create', 'edit', 'view'],

	// Valid post sub-routes
	validPostSubRoutes: [
		'calendar',
		'published',
		'scheduled',
		'drafts',
		'errors',
	],

	// Route building helpers
	getValidServicesForFeature: (feature: string): string[] => {
		return ROUTE_CONFIG.validServices[feature] || [];
	},

	isFeatureValid: (feature: string): boolean => {
		return ROUTE_CONFIG.validFeatures.includes(feature);
	},

	isServiceValidForFeature: (feature: string, service: string): boolean => {
		const validServices = ROUTE_CONFIG.validServices[feature];
		return validServices ? validServices.includes(service) : false;
	},
};

/** Active Data Sources tab segment (e.g. `data-model`), if present in the URL. */
function getDataSourcesSubRoute(url: string): string | undefined {
	const pathOnly = url.split('?')[0].split('#')[0];
	const segments = pathOnly.split('/').filter(Boolean);
	const dataSourcesIdx = segments.indexOf('data-sources');
	if (dataSourcesIdx === -1) {
		return undefined;
	}
	const child = segments[dataSourcesIdx + 1];
	const allowed = ROUTE_CONFIG.specialRoutes['data-sources'].subRoutes;
	return child && allowed.includes(child) ? child : undefined;
}

/** Client id of an active-list row, whichever query produced it. */
function resolveClientRowId(row: {
	id?: string | null;
	clientId?: string | null;
	sortKey?: string | null;
}): string | undefined {
	return row?.id || row?.clientId || clientIdFromIdOrSortKeyTail(row);
}

@Component({
	selector: 'global-clients-dropdown',
	templateUrl: 'global-clients-dropdown.component.html',
	styleUrls: ['./global-clients-dropdown.component.scss'],
	animations: [DropdownToggle()],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class GlobalClientsDropdownComponent implements OnInit, OnDestroy {
	@ViewChild('search') search: ElementRef;
	@ViewChild('dropdownClients') dropdownClients: ElementRef;
	unsubscribe$ = new Subject<void>();
	isActiveClientsFetching$: Observable<boolean>;
	globalActiveClients: fromPortfolioModuleRootStoreAsFeature.IClientDetails[] =
		[];
	tempActiveClients: fromPortfolioModuleRootStoreAsFeature.IClientDetails[];
	searchText: string;
	currentUrl: string;
	dropdownBoxState: string = 'hide';
	selectedClient: any;
	dropdownListHeight: number;
	noResults: boolean = false;
	getAccountData: any;
	users: fromSharedStore.IUser[];
	currentUser: fromSharedStore.IUsersPayload;
	hasSelectedClient: boolean = false;
	constructor(
		private portofolioRootStore: Store<fromPortfolioModuleRootStoreAsFeature.PortfolioModuleReducers>,
		private sharedStore: Store<fromSharedStore.SharedModuleReducers>,
		public matDialog: MatDialog,
		private router: Router,
		private cdRef: ChangeDetectorRef,
		private jepClientsService: JepClientsService,
		private readonly activatedRoute: ActivatedRoute
	) {}
	private clientStore = inject(ClientStore);
	private actions$ = inject(Actions);
	private routeClientLookup = inject(RouteClientLookupService);
	private snackBar = inject(MatSnackBar);
	/** Route client id the active list does not hold; null cancels a lookup. */
	private unlistedRouteClientId$ = new Subject<string | null>();
	/** Extras for routes dispatched while set; see the missing-client redirect. */
	private routeExtras: NavigationExtras | undefined;
	/** Last unlisted route client the API confirmed (e.g. an archived client). */
	private foundRouteClient: fromPortfolioModuleRootStoreAsFeature.IClientDetails | null =
		null;
	ngOnInit(): void {
		this.loadDropdownClients();
		this.autoRun();
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	autoRun(): void {
		this.passCurrentURL();
		this.initSelectDropDownLoadingState();
		this.listeners();
	}

	loadDropdownClients(): void {
		this.portofolioRootStore.dispatch(
			new fromPortfolioModuleRootStoreAsFeature.ReadClientsForDropdown({
				active: [],
				archived: [],
				fetching: false,
				success: false,
				error: false,
			})
		);
	}

	initSelectDropDownLoadingState(): void {
		this.listenOnGetAccount();
	}

	listeners(): void {
		this.listenOnGetUsers();
		this.listenOnActiveClientsFetching();
		this.listenOnGetSelectedClient();
		this.listenOnGlobalGetActiveClients();
		this.listenOnUnlistedRouteClient();
		this.listenToRouterEvents();
	}

	listenOnGetUsers(): void {
		this.sharedStore
			.select(fromSharedStore.getUsers)
			.pipe(
				tap(users => {
					log(
						'global-clients-dropdown listenOnGetUsers: users',
						users
					);
				}),
				filter(users => users !== null),
				markForCheckOnEmit(this.cdRef),
				takeUntil(this.unsubscribe$)
			)
			.subscribe((users: fromSharedStore.IUsersPayload[]) => {
				this.users = users;
			});
	}

	listenOnGetSelectedClient(): void {
		this.portofolioRootStore
			.select(fromPortfolioModuleRootStoreAsFeature.selectedClient)
			.pipe(
				tap(state =>
					log(
						'global-clients-dropdown listenOnGetSelectedClient: ',
						state
					)
				),
				filter(state => state !== undefined && state !== null),
				markForCheckOnEmit(this.cdRef),
				takeUntil(this.unsubscribe$),
				catchError(error => {
					log('Error: portfolio selection:', error);
					return '';
				})
			)
			.subscribe(
				(
					result: fromPortfolioModuleRootStoreAsFeature.IClientDetails
				) => {
					log(
						'global-clients-dropdown listenOnGetSelectedClient: result',
						result
					);
					log(
						'global-clients-dropdown listenOnGetSelectedClient: this.currentUrl',
						this.currentUrl
					);

					safeUpdateInSubscription(this.cdRef, () => {
						if (Boolean(result.selected)) {
							this.selectedClient = result;
							if (result.id) {
								this.syncPortfolioClientToClientStore(result);
							}
						} else {
							if (!Boolean(result.sortKey)) {
								log('getInsightsCount...');
								this.selectedClient = {
									name: 'All Clients',
									value: 'all_clients',
									selected: false,
									logo: this.getAccountData.logo,
								};
							}
						}
					});
				}
			);
	}

	listenOnActiveClientsFetching(): void {
		this.isActiveClientsFetching$ = this.portofolioRootStore
			.select(fromPortfolioModuleRootStoreAsFeature.isClientsFetching)
			.pipe(
				tap(result => {
					log(
						'global-clients-dropdown listenOnActiveClientsFetching: result',
						result
					);
				}),
				markForCheckOnEmit(this.cdRef),
				takeUntil(this.unsubscribe$)
			);
	}

	listenOnGlobalGetActiveClients(): void {
		// Listen for the result from the effect - check both the selector and the raw state
		// Good when refreshing the page
		this.portofolioRootStore
			.select(fromPortfolioModuleRootStoreAsFeature.getAllActiveClients)
			.pipe(
				tap(result => {
					log(
						'global-clients-dropdown listenOnGlobalGetActiveClients: result',
						result
					);
				}),
				filter(result => result !== undefined || result !== null),
				markForCheckOnEmit(this.cdRef),
				takeUntil(this.unsubscribe$)
			)
			.subscribe(
				(
					result: fromPortfolioModuleRootStoreAsFeature.IClientDetails[]
				) => {
					if (
						result &&
						result.length > 0 &&
						result[result.length - 1] !== null
					) {
						log(
							'global-clients-dropdown listenOnGlobalGetActiveClients: result[]',
							result
						);

						this.includeAllClientsAsOption(result);
					}
				}
			);
	}

	includeAllClientsAsOption(
		clients: fromPortfolioModuleRootStoreAsFeature.IClientDetails[]
	): void {
		log('clg includeAllClientsAsOption clients', clients);
		this.globalActiveClients = this.sortAndReferenceAllClients(clients);
		this.tempActiveClients = this.globalActiveClients;
		this.setClientListHeightClass();
		this.cdRef.detectChanges();
	}

	sortAndReferenceAllClients(
		clients: fromPortfolioModuleRootStoreAsFeature.IClientDetails[]
	): fromPortfolioModuleRootStoreAsFeature.IClientDetails[] {
		log(
			'global-clients-dropdown sortAndReferenceAllClients: clients',
			clients
		);
		const sortedClients: any[] = [...clients].sort(dynamicKeySort('name'));
		if (clients.length) {
			sortedClients.unshift({
				name: 'All Clients',
				value: 'all_clients',
				selected: false,
				logo: this.getAccountData.logo,
			});
			let routes = ROUTE_CONFIG.validFeatures;
			let currentRoute: Array<string> = this.router.url.split('/');
			let isMatch = routes.filter(result => result === currentRoute[1]);
			if (isMatch.length) {
				// Global feature URLs (`/data-sources/data-model`) carry no client id;
				// segment [2] there is a tab name, not a client.
				const clientId: string =
					this.activatedRoute.snapshot.params['id'];

				if (!clientId && !this.selectedClient) {
					this.setClientSelected(
						sortedClients[0],
						'sortAndReferenceAllClients:index:0'
					);
				} else if (clientId) {
					const selectedClient = sortedClients.find(client =>
						includes(client.id, clientId)
					);
					if (selectedClient) {
						this.setClientSelected(
							selectedClient,
							'sortAndReferenceAllClients:selectedClient'
						);
					} else if (!this.selectedClient) {
						this.setClientSelected(
							sortedClients[0],
							'sortAndReferenceAllClients:default'
						);
					}
				}
			}
		}
		if (clients.length === 1) {
			const singleClient = clients[clients.length - 1];
			safeUpdateInSubscription(this.cdRef, () => {
				this.selectedClient = singleClient;
			});
			this.setClientSelected(
				singleClient,
				'sortAndReferenceAllClients:default'
			);
		}
		this.hasSelectedClient = true;
		return sortedClients;
	}

	listenToRouterEvents(): void {
		// Create router events stream with initial current route
		const routerEvents$ = this.router.events.pipe(
			filter(event => event instanceof NavigationEnd),
			map(event => event as NavigationEnd),
			startWith({
				id: 1,
				url: this.router.url,
				urlAfterRedirects: this.router.url,
			} as NavigationEnd)
		);

		const activeClients$ = this.portofolioRootStore
			.select(fromPortfolioModuleRootStoreAsFeature.getAllActiveClients)
			.pipe(
				filter(
					clients =>
						clients !== undefined &&
						clients !== null &&
						clients.length > 0 &&
						clients[clients.length - 1] !== null
				)
			);

		combineLatest([routerEvents$, activeClients$])
			.pipe(markForCheckOnEmit(this.cdRef), takeUntil(this.unsubscribe$))
			.subscribe(([event, clients]) => {
				this.processRouteChange(event.urlAfterRedirects, clients);
			});
	}

	listenOnUnlistedRouteClient(): void {
		this.unlistedRouteClientId$
			.pipe(
				distinctUntilChanged(),
				switchMap(clientId =>
					clientId
						? this.routeClientLookup
								.lookup(clientId)
								.pipe(map(result => ({ clientId, result })))
						: EMPTY
				),
				markForCheckOnEmit(this.cdRef),
				takeUntil(this.unsubscribe$)
			)
			.subscribe(({ clientId, result }) =>
				this.onRouteClientLookup(clientId, result)
			);
	}

	/**
	 * The active list can be filtered or stale, so a route client missing from
	 * it is only known to be gone once the API says so. A found client (e.g.
	 * archived) is selected; a missing one sends the page to its All Clients
	 * URL instead of leaving a client URL under an "All Clients" label.
	 */
	private onRouteClientLookup(
		clientId: string,
		result: RouteClientLookup
	): void {
		if (result.status === 'found') {
			this.foundRouteClient = {
				...result.client,
				id: clientId,
				selected: true,
			};
			this.setClientSelected(
				this.foundRouteClient,
				'routeClientLookup:found'
			);
		} else if (result.status === 'missing') {
			this.snackBar.openFromComponent(JepSnackbarComponent, {
				duration: 5000,
				data: {
					text: 'Client not found. Showing All Clients.',
					status: 'error',
				},
			});
			// Replace the missing client's history entry: Back would otherwise
			// reopen it, look it up again and redirect again.
			this.routeExtras = { replaceUrl: true };
			try {
				this.routeAllClientsToGlobalUrl();
			} finally {
				this.routeExtras = undefined;
			}
		}
	}

	private processRouteChange(
		url: string,
		clients: fromPortfolioModuleRootStoreAsFeature.IClientDetails[] | null
	): void {
		if (!clients || clients.length === 0) {
			return;
		}

		log('global-clients-dropdown processRouteChange: ', url, clients);
		this.currentUrl = url;
		let clientId: string = this.activatedRoute.snapshot.params['id'];

		// Fallback: extract client ID from URL path if not available in route params
		if (!clientId && this.currentUrl.includes('/clients/')) {
			const currentRoute: Array<string> = this.currentUrl.split('/');
			clientId = currentRoute.length > 2 ? currentRoute[2] : '';
		}
		log(
			'global-clients-dropdown processRouteChange[client][0]: ',
			clientId,
			clients
		);
		// The active list holds two row shapes: dropdown rows carry `id`,
		// portfolio rows only `clientId`/`sortKey`.
		const client = clientId
			? clients.find(row => resolveClientRowId(row) === clientId)
			: undefined;
		log('global-clients-dropdown processRouteChange[client]: ', client);
		// `/clients/archived` is the archived clients list, not a client id;
		// `/clients/archived/reports` is an unknown client like any other.
		const isArchivedClientsList = /^\/clients\/archived\/?$/.test(
			url.split(/[?#]/)[0]
		);
		const unlistedClientId =
			!client && clientId && !isArchivedClientsList ? clientId : null;
		const foundClient =
			unlistedClientId && this.foundRouteClient?.id === unlistedClientId
				? this.foundRouteClient
				: null;
		if (client) {
			this.setClientSelected(
				{ ...client, id: clientId },
				'listenToRouterEvents:client'
			);
		} else if (foundClient) {
			this.setClientSelected(foundClient, 'listenToRouterEvents:found');
		} else {
			const allClientsOption = {
				name: 'All Clients',
				value: 'all_clients',
				selected: true,
				logo: this.getAccountData.logo,
			};
			safeUpdateInSubscription(this.cdRef, () => {
				this.selectedClient = allClientsOption;
			});

			this.setClientSelected(
				allClientsOption,
				'listenToRouterEvents:default'
			);
		}
		// After the fallback above, so a lookup result is never overwritten by it.
		this.unlistedRouteClientId$.next(foundClient ? null : unlistedClientId);
	}

	toggleDropdownBoxState(event: Event): void {
		this.dropdownBoxState =
			this.dropdownBoxState === 'show' ? 'hide' : 'show';
		this.setClientListHeightClass();
	}

	onClickedOutside(): void {
		this.dropdownBoxState = 'hide';
	}

	onClickSelectClient(
		client: fromPortfolioModuleRootStoreAsFeature.IClientDetails
	) {
		log('global-clients-dropdown onClickSelectClient: client', client);
		if (!Boolean(client)) return;
		// reset the hasSelectedClient flag
		// The route listener will set the client as selected if the client is not selected
		this.hasSelectedClient = false;
		this.dropdownBoxState = 'hide';

		const isAllClients = client.value === 'all_clients';
		const clientId = isAllClients
			? undefined
			: clientIdFromIdOrSortKeyTail(client);
		if (!isAllClients && !clientId) {
			log(
				'global-clients-dropdown onClickSelectClient: row has no client id',
				client
			);
			return;
		}

		if (clientId) {
			this.setClientSelected(
				{ ...client, id: clientId },
				'onClickSelectClient'
			);
			const clientIdFromURL: string = this.currentUrl.split('/')[2];
			log(
				'global-clients-dropdown onClickSelectClient: clientIdFromURL',
				clientIdFromURL
			);
			log(
				'global-clients-dropdown onClickSelectClient: clientId',
				clientId
			);
			if (clientIdFromURL === clientId) {
				if (stringContains(this.currentUrl, '/reports') === true) {
					this.setClientSelected(
						client,
						'onClickSelectClient:reports-same-client'
					);
					this.jepClientsService.getClientDetails(clientId);
				}
				return; // do not re-fetch the data when same route URL
			}
			this.jepClientsService.getClientDetails(clientId);
			this.routeToCurrentURL(clientId);
		} else {
			const allClientsOption = {
				name: 'All Clients',
				value: 'all_clients',
				selected: true,
				logo: this.getAccountData.logo,
			};
			this.setClientSelected(
				allClientsOption,
				'onClickSelectClient:all-clients'
			);

			this.routeAllClientsToGlobalUrl();
		}
	}

	/** Same feature, global (no `clients/:id`) URL for the All Clients selection. */
	private routeAllClientsToGlobalUrl(): void {
		const url = this.currentUrl;
		const hasReportsSegment = url
			.split('/')
			.filter(Boolean)
			.includes('reports');
		// Order matters: the first matching feature wins.
		const featureRoutes: Array<[boolean, () => unknown]> = [
			[url.includes('/kpis'), () => this.routeToGlobalKPIS()],
			[url.includes('/budgets'), () => this.routeToGlobalBudgets()],
			[url.includes('/anomalies'), () => this.routeToGlobalAnomaly()],
			[
				url.includes('/automations'),
				() => this.routeToGlobalAutomation(),
			],
			[url.includes('/insights'), () => this.routeToGlobalInsights()],
			[hasReportsSegment, () => this.routeToGlobalReports()],
			[url.includes('/ai-visibility'), () => this.routeToAiVisibility()],
			[url.includes('/local'), () => this.routeToLocalListings()],
			[url.includes('/reviews'), () => this.routeToReviews()],
			[url.includes('/posts'), () => this.routeToSocialPosts()],
			[url.includes('/data-sources'), () => this.routeToDataSources()],
			[url.includes('/chats'), () => this.routeToChats()],
			[url.includes('/reports'), () => this.routeToReports()],
			[url.includes('/settings'), () => this.routeToSettings()],
		];
		featureRoutes.find(([matches]) => matches)?.[1]();
	}

	/**
	 * `/clients/:id/<feature>[/<tab>]` -> `/<feature>[/<tab>]`. A deeper URL
	 * names one item of that client (a platform, a post), so it goes to
	 * `/<feature>`.
	 */
	private routeToGlobalFeature(): void {
		const routes: string[] = this.currentUrl.split('/');
		this.dispatchToRoute(
			routes.length === 5 ? [routes[3], routes[4]] : [routes[3]]
		);
	}

	/** `/clients/:id/ai-visibility/<tab>` -> `/ai-visibility/<tab>`. */
	routeToAiVisibility(): void {
		this.routeToGlobalFeature();
	}

	routeToLocalListings(): void {
		this.routeToGlobalFeature();
	}

	routeToSettings(): boolean {
		this.dispatchToRoute(['settings', 'account']);
		return true;
	}

	routeToReviews(): void {
		this.routeToGlobalFeature();
	}

	routeToSocialPosts(): void {
		this.routeToGlobalFeature();
	}

	routeToGlobalKPIS() {
		this.dispatchToRoute(['kpis']);
		return true;
	}

	routeToGlobalBudgets() {
		this.dispatchToRoute(['budgets']);
		return true;
	}

	routeToGlobalAnomaly() {
		this.dispatchToRoute(['anomalies']);
		return true;
	}

	routeToGlobalAutomation() {
		this.dispatchToRoute(['automations']);
		return true;
	}

	routeToGlobalInsights() {
		this.dispatchToRoute(['insights']);
		return true;
	}

	routeToGlobalReports(): boolean {
		this.dispatchToRoute(buildGlobalReportsRoute(this.currentUrl));
		return true;
	}

	routeToDataSources() {
		const route = ['data-sources'];
		const subRoute = getDataSourcesSubRoute(this.currentUrl);
		if (subRoute) {
			route.push(subRoute);
		}
		this.dispatchToRoute(route);
		return true;
	}

	routeToChats() {
		this.dispatchToRoute(['chats']);
		return true;
	}

	routeToReports() {
		this.dispatchToRoute(buildGlobalReportsRoute(this.currentUrl));
		return true;
	}

	private routeToClientDataSources(
		clientId: string,
		currentUrl: string
	): void {
		const route = ['clients', clientId, 'data-sources'];
		const subRoute = getDataSourcesSubRoute(currentUrl);
		if (subRoute) {
			route.push(subRoute);
		}
		this.dispatchToRoute(route);
	}

	routeOnArchiveOrActive(
		clientId: string,
		currentUrl: string,
		type: 'kpis' | 'budgets'
	) {
		stringContains(currentUrl, '/archived/list') === true
			? this.dispatchToRoute([
					'clients',
					clientId,
					type,
					'archived',
					'list',
				])
			: this.dispatchToRoute(['clients', clientId, type]);
	}

	routeToCurrentURL(clientId: string): void {
		const currentUrl: string = this.currentUrl;
		if (stringContains(currentUrl, '/kpis') === true) {
			this.routeOnArchiveOrActive(clientId, currentUrl, 'kpis');
		} else if (stringContains(currentUrl, '/data-sources') === true) {
			this.routeToClientDataSources(clientId, currentUrl);
		} else if (stringContains(currentUrl, '/chats') === true) {
			if (currentUrl.includes('/chats/history')) {
				this.dispatchToRoute(['clients', clientId, 'chats', 'history']);
			} else if (currentUrl.includes('/chats/memories')) {
				this.dispatchToRoute([
					'clients',
					clientId,
					'chats',
					'memories',
				]);
			} else if (currentUrl.includes('/chats/rules')) {
				this.dispatchToRoute(['clients', clientId, 'chats', 'rules']);
			} else {
				this.dispatchToRoute(['clients', clientId, 'chats']);
			}
		} else if (stringContains(currentUrl, '/budgets') === true) {
			this.routeOnArchiveOrActive(clientId, currentUrl, 'budgets');
		} else if (this.currentUrl.includes('/anomalies')) {
			this.dispatchToRoute(['clients', clientId, 'anomalies']);
		} else if (this.currentUrl.includes('/automation')) {
			const arrCurrentUrl: string[] = this.currentUrl.split('/');
			if (
				arrCurrentUrl.length == 5 &&
				(arrCurrentUrl.includes('gsc') ||
					arrCurrentUrl.includes('gmb') ||
					arrCurrentUrl.includes('gads') ||
					arrCurrentUrl.includes('ga') ||
					arrCurrentUrl.includes('web') ||
					arrCurrentUrl.includes('fbads') ||
					arrCurrentUrl.includes('mads'))
			) {
				this.dispatchToRoute([
					'clients',
					clientId,
					'automations',
					arrCurrentUrl[4],
				]);
			} else {
				this.dispatchToRoute(['clients', clientId, 'automations']);
			}
		} else if (this.currentUrl.includes('/insights')) {
			this.dispatchToRoute(['clients', clientId, 'insights']);
		} else if (stringContains(currentUrl, '/reports') === true) {
			this.dispatchToRoute(
				buildClientReportsRouteOnSwitch(currentUrl, clientId)
			);
		} else if (stringContains(currentUrl, '/ai-visibility') === true) {
			const splited = currentUrl.split('/');
			const isClient = splited.includes('clients');
			if (isClient === false) {
				splited.unshift('clients');
			} else {
				splited.shift();
			}
			splited[1] = clientId;
			this.dispatchToRoute(splited);
		} else if (stringContains(currentUrl, '/local') === true) {
			const splited = currentUrl.split('/');
			let isClient: boolean = false;
			splited.forEach(item => {
				if (item === 'clients') isClient = true;
			});
			if (isClient === false) {
				splited.unshift('clients');
			} else {
				splited.shift();
			}
			splited[1] = clientId;
			this.dispatchToRoute(splited);
		} else if (stringContains(currentUrl, '/reviews') === true) {
			const splited = currentUrl.split('/');
			let isClient: boolean = false;
			splited.forEach(item => {
				if (item === 'clients') isClient = true;
			});
			if (isClient === false) {
				splited.unshift('clients');
			} else {
				splited.shift();
			}
			splited[1] = clientId;
			this.dispatchToRoute(splited);
		} else if (stringContains(currentUrl, '/posts') === true) {
			const splited = currentUrl.split('/');
			let isClient: boolean = false;
			splited.forEach(item => {
				if (item === 'clients') isClient = true;
			});
			if (isClient === false) {
				splited.unshift('clients');
			} else {
				splited.shift();
			}
			splited[1] = clientId;

			// Handle specific post edit route: /clients/:clientId/posts/:postId/edit
			if (
				splited.length >= 5 &&
				splited[3] === 'posts' &&
				splited[5] === 'edit'
			) {
				// Keep the post ID and edit action when switching clients
				this.dispatchToRoute(splited);
			} else {
				this.dispatchToRoute(splited);
			}
		} else {
			this.routeToClientSettings(clientId, this.currentUrl);
		}
	}

	routeToClientSettings(clientId: string, currentUrl: string): void {
		if (currentUrl.includes('/connections')) {
			this.dispatchToRoute([
				'clients',
				clientId,
				'settings',
				'connections',
			]);
		} else if (currentUrl.includes('/notes')) {
			this.dispatchToRoute(['clients', clientId, 'settings', 'notes']);
		} else if (currentUrl.includes('/pages')) {
			this.dispatchToRoute(['clients', clientId, 'settings', 'pages']);
		} else {
			this.dispatchToRoute(['clients', clientId, 'settings', 'profile']);
		}
	}

	dispatchToRoute(route: string[]): void {
		this.sharedStore.dispatch(
			new fromSharedStore.Go(
				this.routeExtras
					? { path: route, extras: this.routeExtras }
					: { path: route }
			)
		);
	}

	setClientSelected(
		client: fromPortfolioModuleRootStoreAsFeature.IClientDetails,
		sourceFn?: string
	): void {
		log(
			'global-clients-dropdown setClientSelected: sourceFn',
			sourceFn,
			' client ==>',
			client
		);

		if (!client) {
			console.warn(
				'clg global-clients-dropdown setClientSelected: client is undefined/null, skipping'
			);
			return;
		}

		const selectedClient: Client =
			this.portfolioClientToClientStorePayload(client);

		this.clientStore.setSelectedClient(selectedClient);
		const clientDetailMutation: fromPortfolioModuleRootStoreAsFeature.IClientDetails =
			Object.assign({}, client, { selected: true });
		this.portofolioRootStore.dispatch(
			new fromPortfolioModuleRootStoreAsFeature.SelectClient({
				transacting: false,
				success: clientDetailMutation.value === 'all_clients',
				error: false,
				payload: { clientDetails: clientDetailMutation, actions: {} },
			})
		);
	}

	onSearch(event): void {
		let toSearch: string = event.toLowerCase();
		this.globalActiveClients = this.tempActiveClients.filter(
			(result: any) => {
				let name: string = result.name;
				if (name.toLowerCase().includes(toSearch)) return result;
			}
		);
		this.noResults = this.globalActiveClients.length === 0 ? true : false;

		this.setClientListHeightClass();
	}

	passCurrentURL(): void {
		this.currentUrl = this.router.url;
	}

	createNewClient(): void {
		// Close the dropdown before opening the create-client modal
		this.dropdownBoxState = 'hide';
		const dialogRef = this.matDialog.open(GlobalClientCreateComponent);
		this.followCreatedClient(dialogRef);
		this.cdRef.detectChanges();
	}

	/**
	 * A client created from the dropdown becomes the selected client and the
	 * page follows it on the same feature, from a client page or from a global
	 * one (All Clients). A route-bound drawer (e.g. a data source connect form)
	 * therefore never stays on the previous client, and the create flow, which
	 * leaves the created client in the selected-client store slice, agrees with
	 * the selection.
	 * Listens through the dialog's lifetime; the dialog may be closed by
	 * another component, so success is observed on the action stream.
	 */
	private followCreatedClient(
		dialogRef: MatDialogRef<GlobalClientCreateComponent>
	): void {
		const subscription = this.actions$
			.pipe(
				ofType<fromPortfolioModuleRootStoreAsFeature.CreateClientSuccess>(
					fromPortfolioModuleRootStoreAsFeature.ClientActionTypes
						.CreateClientSuccess
				),
				take(1),
				takeUntil(this.unsubscribe$)
			)
			.subscribe(({ payload }) => this.onClickSelectClient(payload));
		dialogRef.afterClosed().subscribe(() => subscription.unsubscribe());
	}

	runWhenSortKeyIsAvailable(
		result: fromPortfolioModuleRootStoreAsFeature.IClientDetails
	): void {
		if (this.router.url.includes('/settings/profile')) {
			this.router.navigate(['clients']);
		} else {
			const clientId: string =
				result.id || sortKeySplitter(result.sortKey, '#', 2);

			// Removed this to prevent the client from being selected again when the page is loaded
			// TODO: check if this is needed
			// this.jepClientsService.getClientIdAndSelectClient(result);
			log(
				'listenOnGetSelectedClient: result: runWhenSortKeyIsAvailable: clientId',
				clientId
			);
			log(
				'listenOnGetSelectedClient: result: runWhenSortKeyIsAvailable: this.router.url',
				this.router.url
			);
			setTimeout(() => {
				this.navigatePage(clientId, this.router.url);
			}, 0);
		}
	}

	navigatePage(clientId: string, currentUrl: string): void {
		// Extract the path segments from the current URL
		const urlSegments = currentUrl.split('/').filter(segment => segment);
		log('global-clients-dropdown navigatePage: urlSegments', urlSegments);
		// Find the main feature route (e.g., 'kpis', 'budgets', 'automations', etc.)
		const mainFeature = this.extractMainFeature(urlSegments);
		log('global-clients-dropdown navigatePage: mainFeature', mainFeature);
		if (mainFeature) {
			this.router.navigate(urlSegments);
		} else {
			// Fallback to default client route
			this.router.navigate(['clients', clientId]);
		}
	}

	private extractMainFeature(urlSegments: string[]): string | null {
		// Look for feature patterns in the URL segments using the route configuration
		for (const segment of urlSegments) {
			if (ROUTE_CONFIG.validFeatures.includes(segment)) {
				return segment;
			}
		}

		return null;
	}

	setClientListHeightClass(): void {
		if (this.dropdownClients) {
			this.removeClass(this.dropdownClients, 'fixed-height');
			this.dropdownListHeight =
				this.dropdownClients.nativeElement.offsetHeight;
			this.dropdownClients.nativeElement.offsetHeight >= 272
				? this.addClass(this.dropdownClients, 'fixed-height')
				: this.removeClass(this.dropdownClients, 'fixed-height');
			this.cdRef.detectChanges();
			if (this.noResults || this.globalActiveClients.length < 5)
				this.removeClass(this.dropdownClients, 'fixed-height');
		}
	}

	removeClass(elem, className): void {
		if (elem.nativeElement.classList.contains(className))
			elem.nativeElement.classList.remove(className);
	}

	addClass(elem, className): void {
		elem.nativeElement.classList.add(className);
	}

	initReadAccount(): void {
		this.sharedStore.dispatch(
			new fromSharedAccountStore.Read({
				sortKey: 'account#v0',
				caller: 'global-clients-dropdown',
			})
		);
	}

	listenOnGetAccount(): void {
		this.sharedStore
			.select(fromSharedAccountStore.getAccountData)
			.pipe(
				filter(result => !isEmpty(result)),
				markForCheckOnEmit(this.cdRef),
				takeUntil(this.unsubscribe$)
			)
			.subscribe(data => {
				this.getAccountData = data;
			});
	}

	private portfolioClientToClientStorePayload(
		client: fromPortfolioModuleRootStoreAsFeature.IClientDetails
	): Client {
		const clientId = client.id;
		return {
			sortKey: client.sortKey,
			id: client.id,
			clientId,
			name: client.name,
			domain: client.domain,
			logo: client.logo,
			dataSources: client.dataSources,
		};
	}

	private syncPortfolioClientToClientStore(
		client: fromPortfolioModuleRootStoreAsFeature.IClientDetails
	): void {
		if (!client.id || client.value === 'all_clients') {
			return;
		}

		const pathOnly = this.currentUrl.split('?')[0].split('#')[0];
		if (pathOnly === '/reports') {
			return;
		}

		this.clientStore.setSelectedClient(
			this.portfolioClientToClientStorePayload(client)
		);
	}
}
